import { Point } from './Point';

/** flash.geom.Rectangle (semantics mirror Flash Player / Ruffle's Rectangle.as). */
export class Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;

  constructor(x: number = 0, y: number = 0, width: number = 0, height: number = 0) {
    this.x = +x;
    this.y = +y;
    this.width = +width;
    this.height = +height;
  }

  get left(): number {
    return this.x;
  }
  set left(v: number) {
    this.width += this.x - v;
    this.x = v;
  }
  get right(): number {
    return this.x + this.width;
  }
  set right(v: number) {
    this.width = v - this.x;
  }
  get top(): number {
    return this.y;
  }
  set top(v: number) {
    this.height += this.y - v;
    this.y = v;
  }
  get bottom(): number {
    return this.y + this.height;
  }
  set bottom(v: number) {
    this.height = v - this.y;
  }
  get topLeft(): Point {
    return new Point(this.x, this.y);
  }
  set topLeft(p: Point) {
    this.width += this.x - p.x;
    this.height += this.y - p.y;
    this.x = p.x;
    this.y = p.y;
  }
  get bottomRight(): Point {
    return new Point(this.right, this.bottom);
  }
  set bottomRight(p: Point) {
    this.width = p.x - this.x;
    this.height = p.y - this.y;
  }
  get size(): Point {
    return new Point(this.width, this.height);
  }
  set size(p: Point) {
    this.width = p.x;
    this.height = p.y;
  }

  clone(): Rectangle {
    return new Rectangle(this.x, this.y, this.width, this.height);
  }
  isEmpty(): boolean {
    return this.width <= 0 || this.height <= 0;
  }
  setEmpty(): void {
    this.x = 0;
    this.y = 0;
    this.width = 0;
    this.height = 0;
  }
  inflate(dx: number, dy: number): void {
    this.x -= dx;
    this.width += 2 * dx;
    this.y -= dy;
    this.height += 2 * dy;
  }
  inflatePoint(p: Point): void {
    this.inflate(p.x, p.y);
  }
  offset(dx: number, dy: number): void {
    this.x += dx;
    this.y += dy;
  }
  offsetPoint(p: Point): void {
    this.x += p.x;
    this.y += p.y;
  }
  contains(x: number, y: number): boolean {
    return x >= this.x && x < this.x + this.width && y >= this.y && y < this.y + this.height;
  }
  containsPoint(p: Point): boolean {
    return this.contains(p.x, p.y);
  }
  containsRect(r: Rectangle): boolean {
    const r1 = r.x + r.width;
    const b1 = r.y + r.height;
    const r2 = this.x + this.width;
    const b2 = this.y + this.height;
    return r.x >= this.x && r.x < r2 && r.y >= this.y && r.y < b2 && r1 > this.x && r1 <= r2 && b1 > this.y && b1 <= b2;
  }
  intersection(r: Rectangle): Rectangle {
    const out = new Rectangle();
    if (this.isEmpty() || r.isEmpty()) return out;
    out.x = Math.max(this.x, r.x);
    out.y = Math.max(this.y, r.y);
    out.width = Math.min(this.x + this.width, r.x + r.width) - out.x;
    out.height = Math.min(this.y + this.height, r.y + r.height) - out.y;
    if (out.width <= 0 || out.height <= 0) out.setEmpty();
    return out;
  }
  intersects(r: Rectangle): boolean {
    if (this.isEmpty() || r.isEmpty()) return false;
    const x = Math.max(this.x, r.x);
    const y = Math.max(this.y, r.y);
    const w = Math.min(this.x + this.width, r.x + r.width) - x;
    const h = Math.min(this.y + this.height, r.y + r.height) - y;
    return w > 0 && h > 0;
  }
  union(r: Rectangle): Rectangle {
    if (this.isEmpty()) return r.clone();
    if (r.isEmpty()) return this.clone();
    const out = new Rectangle();
    out.x = Math.min(this.x, r.x);
    out.y = Math.min(this.y, r.y);
    out.width = Math.max(this.x + this.width, r.x + r.width) - out.x;
    out.height = Math.max(this.y + this.height, r.y + r.height) - out.y;
    return out;
  }
  equals(r: Rectangle): boolean {
    return r.x === this.x && r.y === this.y && r.width === this.width && r.height === this.height;
  }
  copyFrom(r: Rectangle): void {
    this.x = r.x;
    this.y = r.y;
    this.width = r.width;
    this.height = r.height;
  }
  setTo(x: number, y: number, w: number, h: number): void {
    this.x = x;
    this.y = y;
    this.width = w;
    this.height = h;
  }
  toString(): string {
    return `(x=${this.x}, y=${this.y}, w=${this.width}, h=${this.height})`;
  }
}
